"use client";

import { MainLayout } from "@/components/layout/MainLayout";
import { usePathname } from "next/navigation";
import { routes } from "@/constants";
import { FeatureFlagProvider, SessionProvider } from "@/context";
import { WebSocketProvider } from "@/context/WebSocketProvider";
import { useAsPathInitializer } from "@/store";
import { PylonChat } from "@/components/common";
import { DeliverableStatusWebSocketBridge } from "@/hooks/useDeliverableStatusWebSocketSubscription";
import { MeetingPrepStatusWebSocketBridge } from "@/hooks/useMeetingPrepStatusWebSocketSubscription";

// optional object for configuring SDK cookie behavior, currently showing defaults

function routeNeedsSession(pathname: string): boolean {
  const unprotectedRoutes = [
    routes.auth.authenticate,
    routes.auth.forgotPassword,
    routes.auth.resetPassword,
    routes.auth.authorize,
    routes.checkout.index,
  ];
  return !unprotectedRoutes.some((route) => pathname.startsWith(route));
}

function routesWithoutSideNav(pathname: string): boolean {
  const routesThatNeedSideNav = [
    routes.recovery.index,
    routes.inPersonMobile.index,
    routes.onboarding.index,
  ];
  return (
    routesThatNeedSideNav.some((route) => pathname.startsWith(route)) ||
    pathname === "/"
  );
}

export const ClientLayout = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();
  const needsSession = routeNeedsSession(pathname);
  useAsPathInitializer();

  if (needsSession) {
    return (
      <SessionProvider>
        <WebSocketProvider>
          <DeliverableStatusWebSocketBridge />
          <MeetingPrepStatusWebSocketBridge />
          {routesWithoutSideNav(pathname) ? (
            children
          ) : (
            <MainLayout>{children}</MainLayout>
          )}
        </WebSocketProvider>
        <PylonChat />
      </SessionProvider>
    );
  }

  return <FeatureFlagProvider>{children}</FeatureFlagProvider>;
};
